import { CDN } from '../lib/cdn';

export { SOLUTIONS_BREADCRUMB } from './forFilmmakersPage';

export const ENTERPRISE_FAQ = [
  {
    q: 'What is the best AI video platform for enterprise content teams in 2026?',
    a: 'It depends on the work. Avatar tools fit spokesperson videos; clip suites fit single-shot VFX. For brand-consistent narrative video at volume, Induce is purpose-built — a script-to-cut pipeline with SSO, RBAC, audit logs, an API, and full commercial rights.',
  },
  {
    q: 'Does Induce support SSO, SAML, and enterprise authentication?',
    a: 'Yes. SAML 2.0 SSO with Okta, Azure AD, Google Workspace, or any compatible provider, plus OAuth 2.0 for API service accounts — no separate credentials, no shadow IT.',
  },
  {
    q: 'How does Induce handle data security and training opt-out?',
    a: 'Every tier opts out of foundation-model training — your scripts, briefs, and assets never train models. Workspaces are isolated and every generation and export is logged. SOC 2 Type II is in progress, with data-residency options on enterprise agreements.',
  },
  {
    q: 'How is Induce different from Synthesia Enterprise?',
    a: 'Synthesia is avatar-first — a spokesperson reads to camera. Induce is pipeline-first: it breaks a brief or screenplay into shots, holds continuity across the cut, and orchestrates multiple models. Narrative and brand video, not talking heads.',
  },
  {
    q: 'Can we integrate Induce with existing workflows via API?',
    a: 'Yes. A REST API with Node and Python SDKs lets you ingest briefs, trigger breakdowns, poll status, and export programmatically — with webhooks to wire into your approval, DAM, and CMS workflows.',
  },
  {
    q: 'What volume pricing and SLAs does Induce offer?',
    a: 'Volume pricing scales to your output, with enterprise SLAs, a dedicated CSM, and 24/7 support. Talk to sales for a quote based on your usage.',
  },
  {
    q: 'Can we pilot before committing?',
    a: 'Yes. A pilot program is available, with security-review materials on request, so your teams can validate Induce before a full rollout.',
  },
] as const;

export type EnterpriseCellTone = 'dg' | 'do' | 'plain';

export type EnterpriseCompareCell = { tone: EnterpriseCellTone; label: string };

export const ENTERPRISE_COMPARE_ROWS: {
  capability: string;
  induce: EnterpriseCompareCell;
  synthesia: EnterpriseCompareCell;
  runway: EnterpriseCompareCell;
  agency: EnterpriseCompareCell;
}[] = [
  {
    capability: 'Starting point',
    induce: { tone: 'plain', label: 'Brief or screenplay' },
    synthesia: { tone: 'plain', label: 'Avatar script' },
    runway: { tone: 'plain', label: 'Single clip prompt' },
    agency: { tone: 'plain', label: 'Brief + agency intake' },
  },
  {
    capability: 'Script-to-cut pipeline',
    induce: { tone: 'dg', label: 'Full breakdown, auto-continuity' },
    synthesia: { tone: 'do', label: 'No - avatar-first only' },
    runway: { tone: 'do', label: 'Clip-by-clip only' },
    agency: { tone: 'do', label: 'Manual production' },
  },
  {
    capability: 'SSO / SAML authentication',
    induce: { tone: 'dg', label: 'Yes - Okta, Azure AD, Google' },
    synthesia: { tone: 'dg', label: 'Yes - enterprise tier' },
    runway: { tone: 'do', label: 'Partial - enterprise only' },
    agency: { tone: 'do', label: 'Not applicable' },
  },
  {
    capability: 'Role-based access control',
    induce: { tone: 'dg', label: 'Creator · Reviewer · Admin' },
    synthesia: { tone: 'dg', label: 'Team seats + roles' },
    runway: { tone: 'do', label: 'Workspace roles' },
    agency: { tone: 'do', label: 'Not applicable' },
  },
  {
    capability: 'REST API & webhooks',
    induce: { tone: 'dg', label: 'Full API · Node · Python · HTTP' },
    synthesia: { tone: 'do', label: 'Partial - video API only' },
    runway: { tone: 'do', label: 'API on paid tier' },
    agency: { tone: 'do', label: 'Not applicable' },
  },
  {
    capability: 'Training data opt-out',
    induce: { tone: 'dg', label: 'Every tier - explicit' },
    synthesia: { tone: 'dg', label: 'Enterprise tier' },
    runway: { tone: 'do', label: 'Paid tiers only' },
    agency: { tone: 'do', label: 'Contract-dependent' },
  },
  {
    capability: 'Billing model',
    induce: { tone: 'dg', label: 'Single plan · volume pricing' },
    synthesia: { tone: 'do', label: 'Per-seat · per-video credit' },
    runway: { tone: 'do', label: 'Credits expire monthly' },
    agency: { tone: 'do', label: 'Retainer + per-project invoice' },
  },
  {
    capability: 'Best for',
    induce: { tone: 'plain', label: 'Narrative & brand video at scale' },
    synthesia: { tone: 'plain', label: 'Avatar spokesperson content' },
    runway: { tone: 'plain', label: 'Per-clip cinematic control' },
    agency: { tone: 'plain', label: 'High-budget hero productions' },
  },
];

export const ECON_BEFORE = {
  etag: 'Traditional enterprise video',
  title: 'Agency retainers. Production queues.',
  titleEm: 'Budget overruns.',
  rows: [
    { label: 'Cost per finished video minute', value: '$10,000 - $40,000' },
    { label: 'Turnaround per deliverable', value: '3 - 8 weeks' },
    { label: 'Continuity graph - automatic', value: 'Style guides + manual reviews' },
    { label: 'Approval & governance', value: 'Email chains, shared drives' },
    { label: 'Tool management', value: '5-10 separate subscriptions' },
    { label: 'Commercial rights', value: 'Negotiated per deliverable' },
  ],
} as const;

export const ECON_AFTER = {
  etag: 'With Induce Enterprise',
  title: 'Hours to a first cut. One pipeline.',
  titleEm: 'Governance built in.',
  rows: [
    { label: 'Cost per finished video minute', value: '~$300 - $800' },
    { label: 'Turnaround per deliverable', value: 'Hours to days' },
    { label: 'Continuity graph - automatic', value: 'Persistent memory graph - automatic' },
    { label: 'Approval & governance', value: 'Audit logs · webhook-triggered approvals' },
    { label: 'Tool management', value: '1 platform · 1 billing line' },
    { label: 'Commercial rights', value: 'Full rights - every tier, every output' },
  ],
} as const;

export const ENT_PIPE_STAGES: {
  idx: number;
  num: string;
  label: string;
  title: string;
  panelNum: string;
  panelTitle: string;
  body: string;
  tags: string[];
  kind: 'script_to_cut' | 'warehouse' | 'camera_abstract' | 'dawn' | 'final_cut' | 'harbor' | 'train';
  video?: string;
}[] = [
  {
    idx: 0,
    num: '01',
    label: 'Brief',
    title: 'One source of truth',
    panelNum: '01 - BRIEF',
    panelTitle: 'One source of truth',
    body: 'Upload any stakeholder brief, treatment, or script - FDX, PDF, Fountain, or text. The project starts here, not in a Slack thread.',
    tags: ['FDX', 'PDF', 'Fountain', 'Plain text'],
    kind: 'script_to_cut' as const,
    video: CDN.videos.ingest,
  },
  {
    idx: 1,
    num: '02',
    label: 'Guardrails',
    title: 'Breakdown, on brand',
    panelNum: '02 - GUARDRAILS',
    panelTitle: 'Breakdown, on brand',
    body: "Every scene breaks into shots. Brand characters, products, and assets are cast into the department's continuity graph - and the story is logic-checked before a frame renders.",
    tags: ['Brand memory', 'Approved assets', 'Tone lock', 'Continuity'],
    kind: 'warehouse' as const,
    video: CDN.videos.sceneBreakdownAlt,
  },
  {
    idx: 2,
    num: '03',
    label: 'Generate',
    title: 'The right engine, every shot',
    panelNum: '03 - GENERATE',
    panelTitle: 'The right engine, every shot',
    body: 'Each shot routes to the engine built for it - dialogue to Kling, narrative to Seedance, hero frames to Veo, stylized work to Nano Banana - in one timeline.',
    tags: ['Kling', 'Seedance', 'Veo', 'Nano Banana'],
    kind: 'camera_abstract' as const,
    video: CDN.videos.filmDirection,
  },
  {
    idx: 3,
    num: '04',
    label: 'Review',
    title: 'Approve, override, on the record',
    panelNum: '04 - REVIEW',
    panelTitle: 'Approve, override, on the record',
    body: 'Reviewer roles gate what ships. Webhook approvals wire into your workflow, notes land on single shots without re-rolling the cut, and every action is logged.',
    tags: ['Local override', 'Webhook approvals', 'Audit log', 'No re-roll'],
    kind: 'dawn' as const,
    video: CDN.videos.override,
  },
  {
    idx: 4,
    num: '05',
    label: 'Deliver',
    title: 'Export & archive',
    panelNum: '05 - DELIVER',
    panelTitle: 'Export & archive',
    body: 'MP4, ProRes, or EDL - pushed to your DAM or CMS via API, with every export logged by user, timestamp, and metadata.',
    tags: ['MP4', 'ProRes', 'EDL', 'DAM integration'],
    kind: 'final_cut' as const,
    video: CDN.videos.enterpriseBriefApprovedCut,
  },
];

export const ENTERPRISE_FEATURES = [
  {
    kicker: 'Identity',
    title: 'SSO / SAML 2.0',
    body: 'Okta, Azure AD, Google Workspace, or any SAML 2.0 provider, plus OAuth 2.0 for API service accounts. No separate credentials, no shadow IT.',
    tags: ['Okta', 'Azure AD', 'Google'],
    icon: 'lock' as const,
  },
  {
    kicker: 'Access',
    title: 'Role-based access control',
    body: 'Creator, Reviewer, and Admin roles, scoped per workspace - so you define exactly what each member can generate, approve, override, and export.',
    tags: ['Creator', 'Reviewer', 'Admin'],
    icon: 'users' as const,
  },
  {
    kicker: 'Isolation',
    title: 'Departmental workspaces',
    body: 'Marketing, L&D, Comms, and Brand each get an isolated workspace. Characters, assets, and memory graphs never cross departments.',
    tags: ['Marketing', 'L&D', 'Comms'],
    icon: 'grid' as const,
  },
  {
    kicker: 'Compliance',
    title: 'Audit logs & webhooks',
    body: 'Every generation, override, and export is logged with user, timestamp, and metadata. Webhooks wire it into your approval and compliance workflows.',
    tags: ['Full log trail', 'Webhooks'],
    icon: 'mail' as const,
  },
  {
    kicker: 'Integration',
    title: 'REST API & SDKs',
    body: 'A REST API with Node.js, Python, and HTTP access. Ingest briefs, trigger breakdowns, poll status, and export - without touching the UI.',
    tags: ['Node.js', 'Python', 'HTTP'],
    icon: 'code' as const,
  },
  {
    kicker: 'Data',
    title: 'Training opt-out & data rights',
    body: 'Explicit training opt-out on every tier - your scripts, briefs, and assets never train foundation models. SOC 2 Type II in progress.',
    tags: ['Opt-out', 'SOC 2 (in progress)'],
    icon: 'shield' as const,
  },
] as const;

export const ENTERPRISE_USE_CASES = [
  {
    kicker: 'Product marketing',
    title: 'Launch reels & campaign creative',
    body: 'Script a launch and generate a cinematic cut - brand-consistent and multi-format, ready for broadcast, paid, and organic in one pass.',
    tail: '→ Product · Social · Pre-roll',
    kind: 'harbor' as const,
    video: CDN.videos.filmMakerAlt,
  },
  {
    kicker: 'Internal communications',
    title: 'All-hands, change management & exec video',
    body: 'Turn executive scripts into polished internal videos in hours, not weeks - with one visual language across every message and region.',
    tail: '→ All-hands · Town halls · Change comms',
    kind: 'train' as const,
    video: CDN.videos.enterpriseTeamUseCase,
  },
  {
    kicker: 'Learning & development',
    title: 'Training modules & onboarding content',
    body: 'Generate scenario-based training at scale - consistent characters, localized per market, with no per-module production cost.',
    tail: '→ Onboarding · Compliance · Skills',
    kind: 'warehouse' as const,
    video: CDN.videos.ingestAlt,
  },
  {
    kicker: 'Sales enablement',
    title: 'Personalized demo & pitch content',
    body: "Generate demos, use-case vignettes, and proposal reels tailored to each prospect's context - without per-video overhead.",
    tail: '→ Demo video · Pitch reels · Proposals',
    kind: 'dawn' as const,
    video: CDN.videos.enterpriseSalesEnablement,
  },
] as const;

export const SECURITY_DIFFS = [
  {
    n: 'Identity',
    title: 'SSO / SAML 2.0',
    body: 'Okta, Azure AD, Google Workspace, or any SAML 2.0 provider, plus OAuth 2.0 for API service accounts. No separate credentials to manage.',
  },
  {
    n: 'Data',
    title: 'Training opt-out & isolation',
    body: 'Explicit opt-out from foundation-model training on every tier, isolated workspace storage, and audit logs for every generation, override, and export.',
  },
  {
    n: 'Compliance',
    title: 'SOC 2 Type II in progress',
    body: 'SOC 2 Type II audit underway, with data-residency options on enterprise agreements and security-review materials on request.',
  },
] as const;

export const SPEC_WORKFLOW = {
  title: 'Workflow & Output',
  badge: 'Generally available',
  rows: [
    { key: 'Script formats', value: 'FDX · PDF · Fountain · plain text' },
    { key: 'Export formats', value: 'MP4 · ProRes · EDL' },
    { key: 'Models orchestrated', value: 'Kling · Seedance · Veo · Nano Banana' },
    { key: 'Continuity layer', value: 'Persistent per-project memory graph' },
    { key: 'Shot overrides', value: 'Local - no full re-roll', yes: true },
    { key: 'Approval webhooks', value: 'HTTP POST on generation & export', yes: true },
    { key: 'DAM / CMS push', value: 'Via REST API' },
    { key: 'Concurrent projects', value: 'Unlimited' },
  ],
} as const;

export const SPEC_ENTERPRISE = {
  title: 'Enterprise & Compliance',
  badge: 'Enterprise tier',
  rows: [
    { key: 'Authentication', value: 'SSO · SAML 2.0 · OAuth 2.0', yes: true },
    { key: 'Identity providers', value: 'Okta · Azure AD · Google Workspace' },
    { key: 'Access control', value: 'Role-based - Creator · Reviewer · Admin', yes: true },
    { key: 'Workspace isolation', value: 'Departmental - scoped per team', yes: true },
    { key: 'Audit logging', value: 'Every generation, override & export', yes: true },
    { key: 'Training opt-out', value: 'Every tier - explicit', yes: true },
    { key: 'Commercial rights', value: 'Every tier, every output', yes: true },
    { key: 'Volume pricing', value: 'Available on request' },
  ],
} as const;

export const DASHBOARD_PROJECTS = [
  { name: 'Q3 Product Launch - Hero Reel', meta: '12 scenes · Kling · Veo · 4m ago', format: 'MP4 · ProRes', status: 'gen' as const },
  { name: 'APAC Campaign - 30s Cut', meta: '8 scenes · Seedance · 2h ago', format: 'MP4', status: 'rev' as const },
  { name: 'Brand Refresh - Manifesto Film', meta: '22 scenes · Kling · Nano Banana · yesterday', format: 'ProRes · EDL', status: 'done' as const },
  { name: 'Social Series - 9:16 Variants', meta: '6 scenes · Veo · 3 days ago', format: 'MP4', status: 'done' as const },
];

export const DASHBOARD_WORKSPACES = ['Marketing', 'L&D', 'Comms', 'Brand', 'Sales Enablement'] as const;

export const INTEGRATION_LOGOS = [
  'Okta',
  'Azure AD',
  'Google Workspace',
  'Salesforce',
  'Slack',
  'Workday',
  'HubSpot',
] as const;
